import { CommonPageWrapper } from 'admin/components/wrappers';
import {
  FilterPayLoad,
  tabShowResultTabArray,
} from 'admin/pages/ShowIndividual/Components/ShowViewProfile/staticData';
import AdminTab from 'app/components/AdminTab';
import BackButton from 'app/components/BackButton';
import CustomBreadCrumb from 'app/components/CustomBreadCrumb';
import { PageMainLabel } from 'app/components/SHLabel';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { ElementaryPage } from './ElementaryPage';
import useCommonLookUpDatas from 'hooks/useCommonLookupData';
import useShowApi from 'admin/hooks/useShowApi';
import { HighPointPrJrPage } from './HighPointPrJrPage';
import { GeneralLookup, RiderPoint, RiderSplitClass } from 'services/openapi';
import { defaultAddToShowData } from './static';
import { ShowStatusPage } from './ShowStatusPage';
import { ScoreBoardPage } from './ScoreBoardPage';
import LeaderPointPage from './LeaderPointPage';
import AdminCustomButton from 'app/components/AdminCustomButton';
import SimpleReactValidator from 'simple-react-validator';

const ShowResultPage = () => {
  const { showName } = useParams();
  // SimpleReactValidator for scratch date validation
  const simpleValidator = useRef(new SimpleReactValidator());
  const [triggerValidation, setTriggerValidation] = useState(false);
  // Pending rider-point changes (existing flow)
  const pendingPointsRef = useRef<RiderPoint[]>([]);
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);
  const [resetKey, setResetKey] = useState<number>(0);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const prevLoadingRef = useRef<boolean>(false);

  // Pending scratch-date changes keyed by showRiderId so the last
  const pendingScratchRef = useRef<Map<string, RiderSplitClass>>(new Map());
  const [payload, setPayload] =
    React.useState<FilterPayLoad>(defaultAddToShowData);
  const [divisonData, setDivisionData] = React.useState<GeneralLookup[]>();
  const [classData, setClassData] = React.useState<GeneralLookup[]>();
  const [pageSkip, setPageSkip] = React.useState<number | undefined>(0);
  const { id: showId } = useParams();
  const {
    getShowRiderPoints,
    riderShowList,
    addRiderPoint,
    getSeniorJuniorHighPoints,
    loading,
    riderPointsLoading,
    highPointLoading,
    elementaryHighPointLoading,
    scoreBoardLoading,
    leaderPointLoading,
    highPointList,
    getShowScoreBoard,
    showScoreBoardList,
    getShowLeaderPoint,
    showLeaderPointList,
    getShowElementaryHighPoint,
    showElementaryHighPointList,
    updateClassSplitApi,
  } = useShowApi();
  const {
    getAllLookupData,
    lookUps,
    loading: lookUpsLoading,
  } = useCommonLookUpDatas();
  const location = useLocation();
  const navigate = useNavigate();
  const { id, tab } = useParams();
  const [tabSelected, setTabSelected] = useState<number>(0);
  const userType = Cookies.get('userType') ?? '';
  const baseUrl =
    userType === 'COACH'
      ? '/coach/shows/show-result'
      : '/admin/shows/show-result';

  const showsResultbreadCrumpitems = [
    {
      id: '',
      text: userType === 'COACH' ? 'Showyeda coach' : 'Showyeda admin',
      disabled: true,
    },
    {
      id: userType === 'COACH' ? '/coach/shows' : '/admin/shows',
      text: 'Shows',
      disabled: false,
    },
    {
      id: '',
      text: 'Enter results',
      disabled: true,
    },
  ];

  const handleBackClick = () => {
    window.history.back();
  };

  const onTabSelectRedirect = e => {
    const { selected } = e;
    Cookies.set('teamsTab', selected);
    switch (selected) {
      case 0:
        navigate(`${baseUrl}/show-status/${id}/${showName}`);
        break;
      case 1:
        navigate(`${baseUrl}/high-point-sr-jr/${id}/${showName}`);
        break;
      case 2:
        navigate(`${baseUrl}/score-board/${id}/${showName}`);
        break;
      case 3:
        navigate(`${baseUrl}/leader-point/${id}/${showName}`);
        break;
      default:
        return '';
    }
  };

  const switchTabsOnBrowserBack = (tab: string) => {
    if (tab) {
      switch (tab) {
        case 'show-status':
          Cookies.set('teamsTab', 0);
          setTabSelected(0);
          setPayload(defaultAddToShowData);
          getShowRiderPoints(
            showId ?? '',
            undefined,
            undefined,
            undefined,
            'team',
          );
          break;
        case 'high-point-sr-jr':
          Cookies.set('teamsTab', 1);
          setTabSelected(1);
          break;
        case 'score-board':
          Cookies.set('teamsTab', 2);
          setTabSelected(2);
          break;
        case 'leader-point':
          Cookies.set('teamsTab', 3);
          setTabSelected(3);
          break;
        default:
          return;
      }
    }
  };

  // ── Scratch-date global save ───────────────────────────────────────────────
  const handleScratchSave = (
    requestBody: RiderSplitClass[],
  ): Promise<boolean> => {
    return updateClassSplitApi(
      false,
      requestBody,
      undefined,
      undefined, // teamFilter – not applicable here
      undefined,
      undefined,
      undefined, // searchInput
      undefined, // sortBy
      undefined, // sortOrder
      pageSkip ?? 1,
      10,
    );
  };

  // ── Global Save ───────────────────────────────────────────────────────────
  const handleGlobalSave = async () => {
    const hasPendingPoints = pendingPointsRef.current.length > 0;
    const hasPendingScratch = pendingScratchRef.current.size > 0;

    if (!hasPendingPoints && !hasPendingScratch) return;

    // Manual validation check for scratch dates
    let hasValidationErrors = false;

    if (hasPendingScratch) {
      const scratchItems = Array.from(pendingScratchRef.current.values());
      for (const item of scratchItems) {
        if (item.scratch && !item.scratchDate) {
          hasValidationErrors = true;
          break;
        }
      }
    }

    // Validate using SimpleReactValidator and show messages
    const isFormValid = simpleValidator.current.allValid();

    if (!isFormValid || hasValidationErrors) {
      simpleValidator.current.showMessages();
      setTriggerValidation(!triggerValidation);
      return;
    }

    const listParameters = {
      showId: showId ?? '',
      divisionId: payload.divisionId.id,
      categoryId: payload.categoryId.id,
      classFilter: payload.classFilter.id
        ? [payload.classFilter.id]
        : undefined,
      pageNumber: pageSkip,
    };

    // Collect the API calls to run in parallel
    const apiCalls: Promise<boolean>[] = [];

    if (hasPendingPoints) {
      apiCalls.push(addRiderPoint(pendingPointsRef.current, listParameters));
      pendingPointsRef.current = [];
    }

    if (hasPendingScratch) {
      apiCalls.push(
        handleScratchSave(Array.from(pendingScratchRef.current.values())),
      );
      pendingScratchRef.current.clear();
    }

    // Hide validation messages and clear pending state optimistically
    simpleValidator.current.hideMessages();
    simpleValidator.current.visibleFields = [];
    setHasPendingChanges(false);
    setIsSaving(true);

    // Only refresh the list once, after ALL APIs succeed
    const results = await Promise.all(apiCalls);
    const allSucceeded = results.every(r => r === true);
    if (allSucceeded) {
      getShowRiderPoints(
        showId ?? '',
        payload.divisionId.id,
        payload.categoryId.id,
        payload.classFilter.id ? [payload.classFilter.id] : undefined,
        'team',
      );
    } else {
      setIsSaving(false);
    }
  };

  // ── Global Cancel ─────────────────────────────────────────────────────────
  const handleGlobalCancel = () => {
    pendingPointsRef.current = [];
    pendingScratchRef.current.clear();
    simpleValidator.current.hideMessages();
    simpleValidator.current.visibleFields = [];
    setHasPendingChanges(false);
    setResetKey(prev => prev + 1);
  };
  useEffect(() => {
    switchTabsOnBrowserBack(tab ?? '');
  }, [location.pathname]);

  useEffect(() => {
    if (showId) {
      getAllLookupData('TEAM_CATEGORY');
      getSeniorJuniorHighPoints(showId);
      getShowScoreBoard(showId);
      getShowLeaderPoint(showId);
      getShowElementaryHighPoint(showId);
    }
  }, [showId, tab]);

  useEffect(() => {
    getShowRiderPoints(
      showId ?? '',
      payload.divisionId.id,
      payload.categoryId.id,
      payload.classFilter.id ? [payload.classFilter.id] : undefined,
      'team',
    );
  }, []);

  return (
    <CommonPageWrapper>
      <HeadingWrapper>
        <StyledPageMainLabel title={showName}>
          Enter Results -({showName})
        </StyledPageMainLabel>
        <CustomBreadCrumb preData={showsResultbreadCrumpitems} />
      </HeadingWrapper>
      <TabWrapper
        className={
          tabSelected === 0
            ? 'show-status'
            : tabSelected === 1
            ? 'high-point-sr-jr'
            : tabSelected === 2
            ? 'score-board'
            : tabSelected === 3
            ? 'leader-point'
            : ''
        }
      >
        <AdminTab
          data={tabShowResultTabArray}
          tabSelected={tabSelected}
          setTabSelected={setTabSelected}
          onTabSelect={onTabSelectRedirect}
        />
        <div className="back-button">
          {tab === 'show-status' && (
            <div className="save-action-wrapper">
              {riderShowList?.showClasses &&
              riderShowList?.showClasses?.length > 0 ? (
                <>
                  <AdminCustomButton
                    buttonType="cancel"
                    onClick={handleGlobalCancel}
                    disabled={!hasPendingChanges}
                  >
                    Cancel
                  </AdminCustomButton>
                  <AdminCustomButton
                    buttonType="save"
                    onClick={handleGlobalSave}
                    disabled={!hasPendingChanges || isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </AdminCustomButton>
                </>
              ) : (
                ''
              )}
            </div>
          )}
          <BackButton onClick={handleBackClick} />
        </div>{' '}
      </TabWrapper>

      {tab === 'show-status' ? (
        <ShowStatusPage
          lookUps={lookUps}
          showId={showId}
          setPayload={setPayload}
          payload={payload}
          setClassData={setClassData}
          getShowRiderPoints={getShowRiderPoints}
          setDivisionData={setDivisionData}
          pageSkip={pageSkip}
          divisonData={divisonData}
          classData={classData}
          loading={riderPointsLoading || lookUpsLoading}
          riderShowList={riderShowList}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
          hasPendingChanges={hasPendingChanges}
          setHasPendingChanges={setHasPendingChanges}
          pendingPointsRef={pendingPointsRef}
          pendingScratchRef={pendingScratchRef}
          prevLoadingRef={prevLoadingRef}
          isPrinting={isPrinting}
          setIsPrinting={setIsPrinting}
          resetKey={resetKey}
          simpleValidator={simpleValidator}
          triggerValidation={triggerValidation}
        />
      ) : tab === 'high-point-sr-jr' ? (
        <HighPointPrJrPage
          highPointList={highPointList}
          loading={highPointLoading || elementaryHighPointLoading}
          showElementaryHighPointList={showElementaryHighPointList}
        />
      ) : tab === 'score-board' ? (
        <ScoreBoardPage
          showScoreBoardList={showScoreBoardList}
          loading={scoreBoardLoading}
        />
      ) : tab === 'leader-point' ? (
        <LeaderPointPage
          showLeaderPointList={showLeaderPointList}
          loading={leaderPointLoading}
        />
      ) : tab === 'elementary' ? (
        <ElementaryPage
          showElementaryHighPointList={showElementaryHighPointList}
        />
      ) : (
        ''
      )}
    </CommonPageWrapper>
  );
};

export default ShowResultPage;
const StyledPageMainLabel = styled(PageMainLabel)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 500px; /* Adjust this value based on your needs */
  min-width: 500px; /* Adjust this value based on your needs */
`;
const HeadingWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px !important;
  @media print {
    display: none;
  }
`;

const TabWrapper = styled.div`
  position: relative;
  @media print {
    display: none;
  }
  &.rides-info {
    .hPRKpe {
      margin: 0 0 2px 0;
    }
  }

  .back-button {
    cursor: pointer;
    position: absolute;
    right: -12px;
    top: 7px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 15px;
    span {
      font-weight: 600;
    }
  }
  .save-action-wrapper {
    display: flex;
    gap: 10px;
    align-items: center;
    @media print {
      display: none;
    }
  }
`;
